import cloneDeep from 'lodash.clonedeep';
import type { ClusterOption, CommonEditorParaState, SchemaDataItem } from '../../types/redux';
import type { EditorParaAction } from './actions';
import {
  EDITOR_ERROR,
  EDITOR_LOADING,
  EDITOR_REMOVE_SCHEMA_DATA,
  EDITOR_RESET_CATALOG_DATA,
  EDITOR_RESET_COMMON_EDITOR_PARA,
  EDITOR_SET_CATALOG_OPTIONS,
  EDITOR_SET_CLUSTER_OPTIONS,
  EDITOR_SET_SCHEMA_CHILDREN,
  EDITOR_SET_SCHEMA_DATA,
  EDITOR_UPDATE_CLUSTER_STATUS,
  EDITOR_UPDATE_DB_SUG_LIST,
} from './types';

const initialState: CommonEditorParaState = {
  catalogOptions: [],
  clusterOptions: [],
  schemaInfo: {},
  schemaData: [],
  error: null,
};

interface ClusterStatusData {
  uuid: string;
  status: string;
  is_deleted?: boolean;
  err?: string;
}

const updateEditorClusterStatus = (
  state: CommonEditorParaState,
  action: { data: ClusterStatusData }
): CommonEditorParaState => {
  const { uuid, status, is_deleted, err } = action.data;
  if (!uuid || !status) {
    return state;
  }
  let updatedState = cloneDeep(state.clusterOptions);
  if (is_deleted) {
    updatedState = (updatedState || []).filter((item: ClusterOption) => item.uuid !== uuid);
  } else {
    updatedState = (updatedState || []).map((item: ClusterOption) => {
      return item.uuid === uuid
        ? {
            ...item,
            status,
            status_message: status,
            error_message: action.data.hasOwnProperty('err') ? err : item?.error_message,
          }
        : item;
    });
  }
  return { ...state, clusterOptions: updatedState };
};

interface UpdateDBSugListAction {
  data: unknown;
  key: string;
  catID: string;
  dbKey?: string;
}

const handleUpdateDBSugList = (
  state: CommonEditorParaState,
  action: UpdateDBSugListAction
): CommonEditorParaState => {
  let updatedSchema = cloneDeep(state.schemaInfo);
  const { data, key, catID, dbKey } = action;
  if (key === 'reset') {
    return { ...state, schemaInfo: {} };
  }
  if (!updatedSchema?.[catID]) {
    updatedSchema = {
      ...updatedSchema,
      [catID]: {
        dbList: [],
        sugList: {},
      },
    };
  }
  if (key === 'dbList') {
    updatedSchema[catID]!.dbList = data as string[];
  } else if (key === 'sugList' && dbKey) {
    updatedSchema[catID]!['sugList'][dbKey] = data;
  }
  return { ...state, schemaInfo: updatedSchema };
};

interface SchemaChildrenData {
  key?: string;
  tableId?: string;
  catalogId?: string;
  id?: string;
  children?: SchemaDataItem[];
  type?: string;
}

const updateSchemaChildren = (
  state: CommonEditorParaState,
  action: { data: SchemaChildrenData }
): CommonEditorParaState => {
  const { data } = action;

  if (data.key === 'reset') {
    return {
      ...state,
      schemaData: [],
    };
  }

  const { tableId, catalogId, id, children, type } = data;
  const currentCt = state.schemaData.find((ct: SchemaDataItem) => ct?.id === `CT-_${catalogId}`);

  switch (type) {
    case 'Catalog':
    case 'database':
    default:
      return {
        ...state,
        schemaData: state.schemaData.map((item: SchemaDataItem) => {
          if (item?.id !== data?.id) {
            return item;
          }
          return {
            ...item,
            children: data.children,
          };
        }),
      };

    case 'TBView':
      return {
        ...state,
        schemaData: state.schemaData.map((ct: SchemaDataItem) => {
          if (ct?.id !== id) {
            return ct;
          }
          return {
            ...ct,
            children: (currentCt?.children || []).map((db: SchemaDataItem) => {
              if (db?.id === tableId) {
                return { ...db, children };
              }
              return db;
            }),
          };
        }),
      };

    case 'tables':
    case 'tables_reset':
      return {
        ...state,
        schemaData: state.schemaData.map((ct: SchemaDataItem) => {
          if (ct?.id !== id) {
            return ct;
          }
          return {
            ...ct,
            children: (currentCt?.children || []).map((db: SchemaDataItem) => {
              if (db?.id !== tableId) {
                return db;
              }
              const clonedDB = cloneDeep(db);
              (clonedDB.children || []).forEach((clDB: SchemaDataItem, index: number) => {
                if (clDB?.name === 'Views') {
                  clonedDB.children![index]!.children =
                    type === 'tables_reset'
                      ? []
                      : (data.children as unknown as { views?: SchemaDataItem[] })?.views || [];
                } else if (clDB?.name === 'Tables') {
                  clonedDB.children![index]!.children =
                    type === 'tables_reset'
                      ? []
                      : (data.children as unknown as { tables?: SchemaDataItem[] })?.tables || [];
                }
              });
              return clonedDB;
            }),
          };
        }),
      };
  }
};

const resetCatalogCacheData = (
  state: CommonEditorParaState,
  action: { catId: string }
): CommonEditorParaState => {
  const updatedSchema = cloneDeep(state.schemaInfo);
  const { catId } = action;
  if (updatedSchema?.[catId]) {
    delete updatedSchema?.[catId];
  }

  return { ...state, schemaInfo: updatedSchema };
};

export const commonEditorPara = (
  state: CommonEditorParaState = initialState,
  action: EditorParaAction
): CommonEditorParaState => {
  switch (action.type) {
    case EDITOR_LOADING:
      return { ...state, loading: action.loading };
    case EDITOR_SET_CATALOG_OPTIONS:
      return { ...state, catalogOptions: action.data };
    case EDITOR_SET_CLUSTER_OPTIONS:
      return { ...state, clusterOptions: action.data };
    case EDITOR_UPDATE_CLUSTER_STATUS:
      return updateEditorClusterStatus(state, action);
    case EDITOR_ERROR:
      return { ...state, error: action.data as string | null };
    case EDITOR_UPDATE_DB_SUG_LIST:
      return handleUpdateDBSugList(state, action);
    case EDITOR_SET_SCHEMA_DATA:
      return { ...state, schemaData: [...action.data] };
    case EDITOR_SET_SCHEMA_CHILDREN:
      return updateSchemaChildren(state, action);
    case EDITOR_REMOVE_SCHEMA_DATA:
      return {
        ...state,
        schemaData: state?.schemaData.filter((sch: SchemaDataItem) =>
          action.data.includes(sch?.id)
        ),
      };
    case EDITOR_RESET_CATALOG_DATA:
      return resetCatalogCacheData(state, action);
    case EDITOR_RESET_COMMON_EDITOR_PARA:
      return initialState;
    default:
      return state;
  }
};
